import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {getSum} from 'util/numbers/getSum';

export interface BiointelligentLinkComponentInfoBannerProps {
  numReferencedPolymersByLinkType: {[linkType in BiointelligentLinkType]: number};
  polymerId: string;
  onDuplicatePolymer: () => Promise<string | null>;
}

export const LINK_TYPE_TO_COMPONENT_NAME: {[linkType in BiointelligentLinkType]: string} = {
  [BiointelligentLinkType.NUCLEOTIDE_PART]: 'part',
  [BiointelligentLinkType.AMINO_ACID_PART]: 'part',
  [BiointelligentLinkType.TRANSCRIPTION]: 'transcription',
  [BiointelligentLinkType.TRANSLATION]: 'translation',
};

export function getLinkContainersSummary(numReferencedPolymersByLinkType: {
  [linkType in BiointelligentLinkType]: number;
}): string {
  const nonZeroReferencedLinkTypes = Object.keys(numReferencedPolymersByLinkType).filter(
    (linkType: BiointelligentLinkType) => numReferencedPolymersByLinkType[linkType] > 0
  );
  assert(
    nonZeroReferencedLinkTypes.length <= 2,
    'Component unexpectedly participates in too many biointelligent link types.'
  );
  return nonZeroReferencedLinkTypes
    .map(
      (linkType: BiointelligentLinkType) =>
        `a ${LINK_TYPE_TO_COMPONENT_NAME[linkType]} in ${numReferencedPolymersByLinkType[linkType]} containing sequences`
    )
    .join(' and ');
}

function BiointelligentLinkComponentInfoBanner({
  numReferencedPolymersByLinkType,
  onDuplicatePolymer,
}: BiointelligentLinkComponentInfoBannerProps): JSX.Element {
  if (getSum(Object.values(numReferencedPolymersByLinkType)) === 0) {
    return <div />;
  }
  const message = `This sequence is referenced as ${getLinkContainersSummary(numReferencedPolymersByLinkType)}. Edit residues by creating a copy of this sequence.`;
  return (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text={message}
      supportingInfo="Optionally update existing links after registering the new copy."
      action={{
        actionType: InlineBannerActionType.ACCORDION_AND_CUSTOM,
        actionText: 'Create copy',
        actionOnClick: onDuplicatePolymer,
      }}
    />
  );
}

export default function QueryingBiointelligentLinkComponentInfoBanner(
  props: Omit<BiointelligentLinkComponentInfoBannerProps, 'numReferencedPolymersByLinkType'>
): JSX.Element {
  const queryResult = useBenchlingQuery<
    BiointelligentLinkContainerCountQuery,
    BiointelligentLinkContainerCountQueryVariables
  >(BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY, {variables: {id: props.polymerId}});
  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <></>;
  }
  const {polymer} = queryResult.data;
  let numReferencedPolymersByLinkType;
  if (polymer.__typename === 'InaccessibleInfoNode') {
    assertAndContinue(
      false,
      'Unexpectedly tried to render BiointelligentLinkComponentInfoBanner for inaccessible sequence'
    );
    return <></>;
  } else if (polymer.__typename === 'SequenceNode') {
    numReferencedPolymersByLinkType = {
      [BiointelligentLinkType.NUCLEOTIDE_PART]: polymer.partContainerCount,
      [BiointelligentLinkType.TRANSCRIPTION]: polymer.transcriptionContainerCount,
      [BiointelligentLinkType.AMINO_ACID_PART]: 0,
      [BiointelligentLinkType.TRANSLATION]: 0,
    };
  } else {
    numReferencedPolymersByLinkType = {
      [BiointelligentLinkType.AMINO_ACID_PART]: polymer.partContainerCount,
      [BiointelligentLinkType.TRANSLATION]: polymer.translationContainerCount,
      [BiointelligentLinkType.NUCLEOTIDE_PART]: 0,
      [BiointelligentLinkType.TRANSCRIPTION]: 0,
    };
  }
  return (
    <BiointelligentLinkComponentInfoBanner
      {...props}
      numReferencedPolymersByLinkType={numReferencedPolymersByLinkType}
    />
  );
}
